import { useState, useEffect, useMemo } from 'react';
import { UserProfile } from '@/shared/types';
import { realDataService } from '@/shared/services/realDataService';
import { ActionType } from '@/features/admin/components/users/UserActionDialog';

type SortField = 'name' | 'email' | 'role' | 'plan' | 'joined' | 'revenue';
type SortDirection = 'asc' | 'desc';

interface UserFilters {
  role: string | null;
  plan: string | null;
}

interface PaginationState {
  currentPage: number;
  perPage: number;
}

export const useAdminUsers = (initialUsers?: UserProfile[]) => {
  const [users, setUsers] = useState<UserProfile[]>(initialUsers || []);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<UserFilters>({ role: null, plan: null });
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [pagination, setPagination] = useState<PaginationState>({ currentPage: 1, perPage: 50 });

  // Action State
  const [actionType, setActionType] = useState<ActionType>(null);
  const [actionUser, setActionUser] = useState<UserProfile | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // Plan Change State
  const [newPlanId, setNewPlanId] = useState<string>('free');
  const [billingPeriod, setBillingPeriod] = useState<string>('monthly');

  // Bulk Selection State
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [bulkActionType, setBulkActionType] = useState<'delete' | 'change_plan' | null>(null);

  // Filter, sort, and paginate users
  const processedUsers = useMemo(() => {
    let result = [...users];

    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(u =>
        u.name.toLowerCase().includes(lowerSearch) ||
        u.email.toLowerCase().includes(lowerSearch)
      );
    }

    // Apply role filter
    if (filters.role) {
      result = result.filter(u => u.role === filters.role);
    }

    // Apply plan filter
    if (filters.plan) {
      result = result.filter(u => u.subscription.planId === filters.plan);
    }

    // Apply sorting
    result.sort((a, b) => {
      let compareValue = 0;

      switch (sortField) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'email':
          compareValue = a.email.localeCompare(b.email);
          break;
        case 'role':
          compareValue = a.role.localeCompare(b.role);
          break;
        case 'plan':
          compareValue = a.subscription.planId.localeCompare(b.subscription.planId);
          break;
        case 'joined':
          compareValue = new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime();
          break;
        case 'revenue':
          // Revenue would need to be fetched separately, using joined date as fallback
          compareValue = new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

    return result;
  }, [users, searchTerm, filters, sortField, sortDirection]);

  // Apply pagination
  const paginatedUsers = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.perPage;
    const endIndex = startIndex + pagination.perPage;
    return processedUsers.slice(startIndex, endIndex);
  }, [processedUsers, pagination]);

  const totalPages = Math.ceil(processedUsers.length / pagination.perPage);
  const totalUsers = processedUsers.length;

  useEffect(() => {
    loadUsers();
  }, []);

  // Reset to first page when filters/sort change
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [searchTerm, filters, sortField, sortDirection]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const allUsers = await realDataService.adminGetAllUsers();
      setUsers(allUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
    setLoading(false);
  };

  const initiateAction = (type: ActionType, user: UserProfile) => {
    setActionType(type);
    setActionUser(user);
    setTempPassword(null);

    // Defaults for plan change
    if (type === 'change_plan') {
      setNewPlanId(user.subscription.planId);
      setBillingPeriod(user.subscription.billingPeriod || 'monthly');
    }
  };

  const closeAction = () => {
    setActionType(null);
    setActionUser(null);
    setTempPassword(null);
  };

  const executeAction = async () => {
    if (!actionUser || !actionType) return;

    try {
      switch (actionType) {
        case 'delete':
          await realDataService.adminDeleteUser(actionUser.id);
          await loadUsers();
          closeAction();
          break;
        case 'reset_password':
          const pass = await realDataService.adminResetUserPassword(actionUser.id);
          setTempPassword(pass);
          // Don't close immediately, show password
          break;
        case 'toggle_company':
          const isCompany = actionUser.subscription.planId === 'company';
          const newStatus = isCompany ? 'free' : 'company';
          await realDataService.adminUpdateUserPlan(actionUser.id, newStatus);
          await loadUsers();
          closeAction();
          break;
        case 'change_plan':
          await realDataService.adminUpdateUserPlan(actionUser.id, newPlanId, billingPeriod);
          await loadUsers();
          closeAction();
          break;
      }
    } catch (error) {
      console.error('Failed to execute action:', error);
      alert('Failed to execute action. Please try again.');
    }
  };

  // Bulk selection functions
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = (userIds: string[]) => {
    setSelectedUserIds(prev => {
      const allSelected = userIds.every(id => prev.has(id));
      return allSelected ? new Set<string>() : new Set(userIds);
    });
  };

  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  const isAllSelected = (userIds: string[]) => {
    return userIds.length > 0 && userIds.every(id => selectedUserIds.has(id));
  };

  const isSomeSelected = (userIds: string[]) => {
    return userIds.some(id => selectedUserIds.has(id));
  };

  const initiateBulkAction = (action: 'delete' | 'change_plan') => {
    setBulkActionType(action);
  };

  const closeBulkAction = () => {
    setBulkActionType(null);
  };

  const executeBulkAction = async () => {
    if (selectedUserIds.size === 0) return;

    try {
      const userIdsArray = Array.from(selectedUserIds);

      if (bulkActionType === 'delete') {
        // Delete all selected users
        await Promise.all(userIdsArray.map(id => realDataService.adminDeleteUser(id)));
        await loadUsers();
      } else if (bulkActionType === 'change_plan') {
        // Change plan for all selected users
        await Promise.all(userIdsArray.map(id =>
          realDataService.adminUpdateUserPlan(id, newPlanId, billingPeriod)
        ));
        await loadUsers();
      }

      clearSelection();
      closeBulkAction();
    } catch (error) {
      console.error('Failed to execute bulk action:', error);
      alert('Failed to execute bulk action. Please try again.');
    }
  };

  const setRoleFilter = (role: string | null) => {
    setFilters(prev => ({ ...prev, role }));
  };

  const setPlanFilter = (plan: string | null) => {
    setFilters(prev => ({ ...prev, plan }));
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to asc
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const goToPage = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const nextPage = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: Math.min(prev.currentPage + 1, totalPages)
    }));
  };

  const prevPage = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: Math.max(prev.currentPage - 1, 1)
    }));
  };

  return {
    users,
    loading,
    searchTerm,
    filteredUsers: paginatedUsers,
    allFilteredUsers: processedUsers,
    actionType,
    actionUser,
    tempPassword,
    newPlanId,
    billingPeriod,
    filters,
    sortField,
    sortDirection,
    pagination,
    totalPages,
    totalUsers,
    setSearchTerm,
    setRoleFilter,
    setPlanFilter,
    setNewPlanId,
    setBillingPeriod,
    handleSort,
    initiateAction,
    closeAction,
    executeAction,
    loadUsers,
    goToPage,
    nextPage,
    prevPage,
    // Bulk actions
    selectedUserIds,
    bulkActionType,
    toggleUserSelection,
    toggleSelectAll,
    clearSelection,
    isAllSelected,
    isSomeSelected,
    initiateBulkAction,
    closeBulkAction,
    executeBulkAction,
  };
};